import { useEffect, useState } from 'react'
import { Typography, Button, MenuItem } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { ASSESSMENT_SELF } from 'routes/constants'
import { getDisplayScore } from 'utils/formatText'
import { useTranslation } from 'react-i18next'
import AssessmentTable from './AssessmentTable'
import { ColStack } from 'components/atoms/container/InCardStack'
import CommonDialog from 'components/molecules/notificationOverlay/CommonDialog'
import RowStack from 'components/atoms/container/RowStack'
import { formatDate } from 'utils/formatDateTime'
import { Tag } from 'components/atoms/Text'
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded'
import { IconContainer } from 'components/atoms/Icons'
import moment from 'moment'
import { StyledSelect } from 'components/atoms/input/StyledInputs'
import AssessmentBehaviourPopup from 'components/organisms/assessments/AssessmentBehaviourPopup'
import colors from 'styles/colors'

const AssessmentChange = ({
  data,
  disableBehaviorRedirect = false,
  internal = false
}) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [currentData, setCurrentData] = useState(null)
  const [prevData, setPrevData] = useState(null)
  const [selfDisplayData, setSelfDisplayData] = useState({})
  const [otherDisplayData, setOtherDisplayData] = useState({})
  const [hasOlderData, setHasOlderData] = useState(false)
  const [behaviourInfoData, setBehaviourInfoData] = useState([])
  const [openAssessmentNote, setOpenAssessmentNote] = useState(false)
  const [openDetails, setOpenDetails] = useState(false)
  const [selectedBehaviour, setSelectedBehaviour] = useState(null)
  const roundDigit = internal ? 2 : 1

  const getDisplayData = (newerData, olderData, scoreField) => {
    const newDisplayData = getDisplayScore(
      olderData ? olderData[scoreField] : null,
      newerData[scoreField],
      roundDigit
    )
    if (olderData) {
      newDisplayData.oldTimeStamp = olderData.ratingTimeStamp
    }
    newDisplayData.newTimeStamp = newerData.ratingTimeStamp
    return newDisplayData
  }

  const getBehaviourInfoData = (scoreField, behaviorScoreField) => {
    const hasOlderData = prevData ? prevData[scoreField] : null

    return [
      ...currentData?.behaviourInfoData.map((behavior, index) => {
        const hasOldScore =
          hasOlderData && prevData.behaviourInfoData[index][behaviorScoreField]
        const oldScore = hasOldScore
          ? prevData.behaviourInfoData[index][behaviorScoreField].toFixed(
              roundDigit
            )
          : '-'
        const newScore =
          behavior[behaviorScoreField]?.toFixed(roundDigit) || '-'

        return [behavior.name, oldScore, newScore]
      })
    ]
  }

  useEffect(() => {
    if (currentData) {
      const selfDD = getDisplayData(
        currentData,
        prevData,
        'selfAllBehaviourAvg'
      )
      const otherDD = getDisplayData(
        currentData,
        prevData,
        'othersAllBehaviourAvg'
      )

      setSelfDisplayData(selfDD)
      setOtherDisplayData(otherDD)
      if (prevData) {
        setHasOlderData(true)
      } else {
        setHasOlderData(false)
      }

      // Get behaviour info data
      const newData = {
        self: {
          scoreField: 'selfAllBehaviourAvg',
          data: getBehaviourInfoData('selfAllBehaviourAvg', 'avgSelfRating')
        },
        others: {
          scoreField: 'othersAllBehaviourAvg',
          data: getBehaviourInfoData('othersAllBehaviourAvg', 'avgOthersRating')
        }
      }

      setBehaviourInfoData(newData)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData, prevData])

  useEffect(() => {
    setCurrentData(data[0])
    setPrevData(data[1])
  }, [data])

  const handleRowClick = behavior => {
    if (!disableBehaviorRedirect) {
      setSelectedBehaviour(behavior)
      setOpenDetails(true)
    }
  }

  const gotoSelfAssessment = () => {
    navigate(ASSESSMENT_SELF, {
      state: {
        goalId: currentData?.goalData.id
      }
    })
  }

  const getFormattedDate = date => {
    return formatDate(date, i18n.resolvedLanguage)
  }

  const CompareToTag = () => (
    <Tag
      sx={{ paddingY: 2, backgroundColor: colors.MetalGrey75 }}
      component={'div'}>
      <RowStack spacing={2}>
        <Typography
          variant='eyebrow'
          fontSize={11}
          lineHeight={'110%'}
          color={colors.MidnightTeal300}>
          {prevData
            ? t('assessmentTimeline.comparedTo')
            : t('assessmentTimeline.noPastAssessments')}
        </Typography>

        {prevData && (
          <IconContainer
            icon={<ArrowRightAltRoundedIcon />}
            size={16}
            style={{ color: colors.MidnightTeal300 }}
          />
        )}
      </RowStack>
    </Tag>
  )

  return (
    <>
      <ColStack>
        <RowStack justifyContent={'space-between'}>
          <Typography variant='h2'>
            {t('assessmentTimeline.assessingChange')}
          </Typography>
          <Button
            size='small'
            onClick={() => gotoSelfAssessment()}
            disabled={
              currentData &&
              (currentData?.isOpen ||
                moment
                  .utc()
                  .diff(moment.utc(currentData?.ratingTimeStamp), 'days') < 14)
            }>
            {t('assessmentTimeline.takeNewSelfassessment')}
          </Button>
        </RowStack>

        <RowStack justifyContent={'space-between'}>
          <RowStack spacing={12} width={'auto'}>
            <Typography variant='h3' color='textSecondary'>
              {getFormattedDate(currentData?.ratingTimeStamp)}
            </Typography>

            <CompareToTag />
            {prevData && (
              <StyledSelect
                value={prevData?.id}
                onChange={e => {
                  setPrevData(data.find(r => r.id === e.target.value))
                }}
                renderValue={selected => {
                  return (
                    <Typography variant='selectionText' noWrap>
                      {getFormattedDate(
                        data.find(r => r.id === selected)?.ratingTimeStamp
                      )}
                    </Typography>
                  )
                }}
                sx={{
                  width: 225
                }}>
                {data
                  .filter(r => r.id !== currentData?.id)
                  .map(rating => (
                    <MenuItem value={rating.id} key={`rating-${rating.id}`}>
                      {getFormattedDate(rating?.ratingTimeStamp)}
                    </MenuItem>
                  ))}
              </StyledSelect>
            )}
          </RowStack>

          <RowStack spacing={6} width={'auto'}>
            {currentData?.goalData.enableOthersAssessment && (
              <Typography
                variant='body1KindaSmall'
                color={colors.MidnightTeal300}>
                {currentData?.othersRatingStats}
              </Typography>
            )}

            <Typography
              variant='body1KindaSmall'
              color={colors.MidnightTeal300}>
              {currentData?.isOpen
                ? t('assessmentTimeline.closing')
                : t('assessmentTimeline.closedOn')}
              {` ${getFormattedDate(currentData?.closingTimeStamp)}`}
            </Typography>
          </RowStack>
        </RowStack>

        <RowStack spacing={12} alignItems={'flex-start'}>
          <AssessmentTable
            displayData={selfDisplayData}
            hasOlderScore={hasOlderData && selfDisplayData.oldScore > 0}
            state={'self'}
            behaviourInfoData={behaviourInfoData}
            handleRowClick={handleRowClick}
          />

          <AssessmentTable
            displayData={otherDisplayData}
            hasOlderScore={hasOlderData && otherDisplayData.oldScore > 0}
            state={'others'}
            behaviourInfoData={behaviourInfoData}
            handleRowClick={handleRowClick}
          />
        </RowStack>
      </ColStack>

      <CommonDialog
        open={openAssessmentNote}
        onClose={() => setOpenAssessmentNote(false)}
        notificationText={t(
          'assessmentTimeline.alreadyCompletedSelfassessment'
        )}
        confirmText={t('common.ok')}
      />

      <AssessmentBehaviourPopup
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        active={1} // Open Comparison tab
        assessmentData={currentData}
        prevAssessmentData={prevData}
        behaviour={selectedBehaviour}
      />
    </>
  )
}

export default AssessmentChange
